/**
 * Formatieren beim „Text ändern“ in der Werkstatt — wie in Word: fett, kursiv,
 * unterstrichen, Farben und Markierung aus dem Brand-Design, Links und Emojis.
 *
 * Arbeitet im Rahmen der Bühne (iframe) auf genau dem Textblock, in dem die
 * Auswahl steht ([data-za-block], siehe editierbarMachen) — nie über
 * Blockgrenzen. Farben, Markierung und Links bekommen dasselbe HTML wie im
 * Assistenten (render.ts: formatieren(), Links mit farbigem <span>), damit
 * Dark-Mode-Klassen und Farben in allen Postfächern passen.
 *
 * Was hier entsteht, wird wie jede Änderung über aenderungenErmitteln() zum
 * Vorschlag (nachher_html), beim Übernehmen bereinigt und nur mit neuen Links
 * durchgelassen, die neuerLinkErlaubt() bestehen.
 */
import { BRAND, MARKIERT, TEXT_FARBEN, type TextFarbe } from '../mailBuilder/render';
import { bereinigen } from './anwenden';
import { neuerLinkErlaubt } from './regeln';

export type Format = 'fett' | 'kursiv' | 'unterstrichen';
export type Farbe = TextFarbe | 'markiert';

export interface FormatZustand {
  block: HTMLElement;
  /** Text ist markiert (nicht nur die Schreibmarke gesetzt) */
  auswahl: boolean;
  fett: boolean;
  kursiv: boolean;
  unterstrichen: boolean;
  /** Adresse des Links, in dem (oder über dem) die Auswahl steht */
  link: string | null;
  /** Auswahl im Rahmen (px, Dokument-Koordinaten) */
  links: number;
  rechts: number;
  oben: number;
  unten: number;
}

/** Emojis zum Einfügen — die üblichen aus den Randori-Mails. */
export const EMOJIS: readonly string[] = [
  '🥋', '🥊', '🤼', '🏆', '🥇', '🥈', '🥉', '🏅', '💪', '🔥', '⭐', '✨',
  '✅', '❗', '❓', '⚠️', '👉', '📋', '📅', '⏰', '📍', '📣', '💡', '📸',
  '🎉', '🎁', '🎄', '☀️', '🍂', '❤️', '🙏', '👍', '😊', '😉', '👧', '👦',
];

const BEFEHL: Record<Format, string> = { fett: 'bold', kursiv: 'italic', unterstrichen: 'underline' };

/** Hilfsfarbe beim Färben — wird sofort durch die Hausfarbe ersetzt. */
const MARKE_HEX = '#010203';
const MARKE = /^\s*(rgb\(\s*1\s*,\s*2\s*,\s*3\s*\)|#010203)\s*$/i;
const MARKE_IM_STIL = /(^|;)\s*(color|background-color|background)\s*:\s*(rgb\(\s*1\s*,\s*2\s*,\s*3\s*\)|#010203)\s*(?=;|$)/gi;
/** Platzhalter-Adresse beim Verlinken — wird sofort durch die echte ersetzt. */
const LINK_MARKE = 'https://za-link.invalid/';

/** Stil eines neuen Links — wie die Links im Assistenten. */
const LINK_STIL = `color: ${BRAND.red}; text-decoration: underline;`;
const LINK_SPAN_STIL = `color: ${BRAND.red};`;
const LINK_SPAN_KLASSE = TEXT_FARBEN.rot.klasse;

function elementVon(n: Node | null): Element | null {
  if (!n) return null;
  return n.nodeType === 1 ? (n as Element) : n.parentElement;
}

function blockVon(n: Node | null): HTMLElement | null {
  return (elementVon(n)?.closest('[data-za-block]') as HTMLElement | null) ?? null;
}

/** Aktuelle Auswahl, wenn sie ganz in einem bearbeitbaren Textblock liegt. */
export function auswahlImBlock(doc: Document): { range: Range; block: HTMLElement } | null {
  const sel = doc.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  const block = blockVon(range.startContainer);
  if (!block || blockVon(range.endContainer) !== block) return null;
  return { range, block };
}

/** Link, in dem die Auswahl steht oder den sie berührt (nur im Block). */
function linkVon(range: Range, block: HTMLElement): HTMLAnchorElement | null {
  const innen = elementVon(range.commonAncestorContainer)?.closest('a');
  if (innen && block.contains(innen)) return innen as HTMLAnchorElement;
  for (const a of Array.from(block.querySelectorAll('a'))) if (range.intersectsNode(a)) return a as HTMLAnchorElement;
  return null;
}

function befehlStand(doc: Document, befehl: string): boolean {
  try { return doc.queryCommandState(befehl); } catch { return false; }
}

/** Was die Leiste über der Auswahl zeigt: Stand von fett/kursiv/unterstrichen, Link, Lage. */
export function formatZustand(doc: Document): FormatZustand | null {
  const a = auswahlImBlock(doc);
  if (!a) return null;
  const { range, block } = a;
  let rect = range.getBoundingClientRect();
  if (!rect.width && !rect.height) {
    // Schreibmarke ohne Ausdehnung (z. B. am Zeilenanfang): erstes Rechteck oder der Block
    const liste = range.getClientRects();
    rect = liste.length ? liste[0] : block.getBoundingClientRect();
  }
  const sx = doc.defaultView?.scrollX ?? 0;
  const sy = doc.defaultView?.scrollY ?? 0;
  return {
    block,
    auswahl: !range.collapsed && range.toString().trim() !== '',
    fett: befehlStand(doc, 'bold'),
    kursiv: befehlStand(doc, 'italic'),
    unterstrichen: befehlStand(doc, 'underline'),
    link: linkVon(range, block)?.getAttribute('href') ?? null,
    links: rect.left + sx,
    rechts: rect.right + sx,
    oben: rect.top + sy,
    unten: rect.bottom + sy,
  };
}

/** Gemerkte Auswahl wiederherstellen (ein Klick in die Leiste nimmt dem Rahmen den Fokus). */
function auswahlHerstellen(doc: Document, gemerkt: Range | null): { range: Range; block: HTMLElement } | null {
  if (gemerkt) {
    const block = blockVon(gemerkt.startContainer);
    if (!block || blockVon(gemerkt.endContainer) !== block || !block.isConnected) return null;
    block.focus({ preventScroll: true });
    const sel = doc.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(gemerkt);
    }
  }
  return auswahlImBlock(doc);
}

function ausfuehren(doc: Document, befehl: string, wert?: string, mitCss = false): boolean {
  try {
    doc.execCommand('styleWithCSS', false, mitCss ? 'true' : 'false');
    return doc.execCommand(befehl, false, wert);
  } catch {
    return false;
  } finally {
    try { doc.execCommand('styleWithCSS', false, 'false'); } catch { /* ältere Browser */ }
  }
}

/** Fett, kursiv oder unterstrichen an- bzw. ausschalten. */
export function formatUmschalten(doc: Document, f: Format, gemerkt: Range | null): boolean {
  const a = auswahlHerstellen(doc, gemerkt);
  if (!a) return false;
  return ausfuehren(doc, BEFEHL[f]);
}

/** Fett, kursiv, Farben, Markierung … in der Auswahl entfernen (Links bleiben). */
export function formatEntfernen(doc: Document, gemerkt: Range | null): boolean {
  const a = auswahlHerstellen(doc, gemerkt);
  if (!a || a.range.collapsed) return false;
  return ausfuehren(doc, 'removeFormat');
}

function farbStil(f: Farbe): { stil: string; klasse: string } {
  if (f === 'markiert') return { stil: MARKIERT, klasse: 'dark-mark-fix' };
  return { stil: `color: ${TEXT_FARBEN[f].hex};`, klasse: TEXT_FARBEN[f].klasse };
}

/** rgb(…) aus dem Browser zurück in #RRGGBB — wie überall in den Mails. */
function rgbZuHex(stil: string): string {
  return stil.replace(/rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/gi, (_m, r: string, g: string, b: string) =>
    `#${[r, g, b].map((x) => Math.min(255, Number(x)).toString(16).padStart(2, '0')).join('').toUpperCase()}`);
}

/** Dark-Mode-Farbklassen, die einer neuen Farbe im Weg stünden. */
function fremdeFarbklassen(el: Element, f: Farbe): void {
  if (f === 'markiert') return;
  for (const k of Array.from(el.classList)) if (/^dark-text-/.test(k)) el.classList.remove(k);
}

/** Ersetzt die Hilfsfarbe im Block durch die Hausfarbe (samt Dark-Mode-Klasse). */
function markeErsetzen(doc: Document, block: HTMLElement, f: Farbe): void {
  const { stil, klasse } = farbStil(f);
  for (const el of Array.from(block.querySelectorAll('*'))) {
    if (el.localName === 'font' && MARKE.test(el.getAttribute('color') ?? '')) {
      // ohne styleWithCSS: <font color> → <span> wie im Assistenten
      const span = doc.createElement('span');
      span.className = klasse;
      span.setAttribute('style', stil);
      while (el.firstChild) span.appendChild(el.firstChild);
      el.replaceWith(span);
      continue;
    }
    const s = el.getAttribute('style');
    if (!s) continue;
    MARKE_IM_STIL.lastIndex = 0;
    if (!MARKE_IM_STIL.test(s)) continue;
    MARKE_IM_STIL.lastIndex = 0;
    const rest = s.replace(MARKE_IM_STIL, '$1').replace(/^[\s;]+|[\s;]+$/g, '').replace(/;\s*;/g, ';');
    if (el.localName === 'span' && !rest && !el.getAttribute('class')) {
      // vom Browser neu angelegt: genau wie im Assistenten
      el.setAttribute('style', stil);
      el.className = klasse;
    } else {
      // vorhandenes Element mit eigener Gestaltung: nur die Farbe dazu — beim
      // Markieren bleibt eine eigene Schriftfarbe (z. B. Randori-Rot) stehen
      const eigeneFarbe = /(^|;)\s*color\s*:/i.test(rest);
      const neu = f === 'markiert' && eigeneFarbe ? stil.replace(/(^|;\s*)color\s*:[^;]*;\s*/i, '$1') : stil;
      el.setAttribute('style', rgbZuHex(`${rest ? `${rest}; ` : ''}${neu}`));
      fremdeFarbklassen(el, f);
      el.classList.add(klasse);
    }
  }
}

/** Auswahl in Randori-Rot, Signalrot, Grau färben oder markieren wie mit dem Textmarker. */
export function farbeSetzen(doc: Document, f: Farbe, gemerkt: Range | null): boolean {
  const a = auswahlHerstellen(doc, gemerkt);
  if (!a || a.range.collapsed) return false;
  const { block } = a;
  // Der Browser darf den Block selbst nicht umgestalten — sonst zurück und innen färben
  const blockStil = block.getAttribute('style');
  const ok = f === 'markiert'
    ? ausfuehren(doc, 'hiliteColor', MARKE_HEX, true) || ausfuehren(doc, 'backColor', MARKE_HEX, true)
    : ausfuehren(doc, 'foreColor', MARKE_HEX, true);
  if (block.getAttribute('style') !== blockStil) {
    if (blockStil === null) block.removeAttribute('style');
    else block.setAttribute('style', blockStil);
    const span = doc.createElement('span');
    span.setAttribute('style', f === 'markiert' ? `background-color: ${MARKE_HEX}` : `color: ${MARKE_HEX}`);
    while (block.firstChild) span.appendChild(block.firstChild);
    block.appendChild(span);
  }
  markeErsetzen(doc, block, f);
  return ok;
}

/**
 * Eingabe aus dem Link-Feld prüfen: ohne Schema wird https:// davorgesetzt,
 * eine Mailadresse wird zu mailto:. Liefert die Adresse oder null.
 */
export function linkAdresse(eingabe: string): string | null {
  let s = eingabe.trim();
  if (!s) return null;
  if (/^[^\s@/:]+@[^\s@/:]+\.[a-z]{2,}$/i.test(s)) s = `mailto:${s}`;
  else if (!/^[a-z][a-z0-9+.-]*:/i.test(s)) s = `https://${s.replace(/^\/+/, '')}`;
  return neuerLinkErlaubt(s) ? s : null;
}

/** Link mit farbigem <span> darin, wie im Assistenten. */
function linkGestalten(doc: Document, a: Element, href: string): void {
  a.setAttribute('href', href);
  a.setAttribute('style', LINK_STIL);
  const einzig = a.children.length === 1 && a.childNodes.length === 1 ? a.firstElementChild : null;
  if (einzig?.localName === 'span' && einzig.classList.contains(LINK_SPAN_KLASSE)) return;
  const span = doc.createElement('span');
  span.className = LINK_SPAN_KLASSE;
  span.setAttribute('style', LINK_SPAN_STIL);
  while (a.firstChild) span.appendChild(a.firstChild);
  a.appendChild(span);
}

/**
 * Auswahl verlinken. Steht die Auswahl schon in einem Link, bekommt dieser die
 * neue Adresse. Ohne markierten Text gibt es keinen neuen Link.
 */
export function linkSetzen(doc: Document, href: string, gemerkt: Range | null): 'ok' | 'ohne_auswahl' | 'ungueltig' | 'fehler' {
  if (!neuerLinkErlaubt(href)) return 'ungueltig';
  const a = auswahlHerstellen(doc, gemerkt);
  if (!a) return 'fehler';
  const vorhanden = linkVon(a.range, a.block);
  if (vorhanden) {
    vorhanden.setAttribute('href', href);
    return 'ok';
  }
  if (a.range.collapsed || !a.range.toString().trim()) return 'ohne_auswahl';
  const marke = `${LINK_MARKE}${Math.random().toString(36).slice(2)}`;
  ausfuehren(doc, 'createLink', marke);
  const neu = Array.from(a.block.querySelectorAll('a')).filter((x) => x.getAttribute('href') === marke);
  if (!neu.length) return 'fehler';
  for (const x of neu) linkGestalten(doc, x, href);
  return 'ok';
}

/** Link aufheben — der Text bleibt (ohne die Linkfarbe). */
export function linkEntfernen(doc: Document, gemerkt: Range | null): boolean {
  const a = auswahlHerstellen(doc, gemerkt);
  if (!a) return false;
  const link = linkVon(a.range, a.block);
  if (!link) return false;
  const auspacken = (e: Element) => {
    const p = e.parentNode;
    if (!p) return;
    while (e.firstChild) p.insertBefore(e.firstChild, e);
    e.remove();
  };
  const einzig = link.children.length === 1 && link.childNodes.length === 1 ? link.firstElementChild : null;
  if (einzig?.localName === 'span' && einzig.classList.contains(LINK_SPAN_KLASSE)) auspacken(einzig);
  auspacken(link);
  a.block.normalize();
  return true;
}

/** Emoji an der Schreibmarke einfügen (ersetzt markierten Text). */
export function emojiEinfuegen(doc: Document, emoji: string, gemerkt: Range | null): boolean {
  const a = auswahlHerstellen(doc, gemerkt);
  if (!a) return false;
  return ausfuehren(doc, 'insertText', emoji);
}

// ── Vorschau auf der Karte ──────────────────────────────────────────────────

const VORSCHAU_TAGS = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'br', 'span', 'a', 'font', 'small', 'sup', 'sub', 'mark']);
const VORSCHAU_STILE = new Set(['color', 'background-color', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-line']);

/**
 * Neue Fassung einer Stelle für die Karte (außerhalb des abgeschotteten
 * Rahmens): nur Text-Auszeichnung, Stile nur Farbe/Hintergrund/fett/kursiv/
 * unterstrichen, keine Bilder; Links öffnen sich in einem neuen Tab.
 */
export function formatVorschau(nachherHtml: string): string {
  const tpl = document.createElement('template');
  tpl.innerHTML = bereinigen(nachherHtml);
  const laufen = (wurzel: Node) => {
    for (const k of Array.from(wurzel.childNodes)) {
      if (k.nodeType === 3) continue;
      if (k.nodeType !== 1) { k.parentNode?.removeChild(k); continue; }
      const e = k as HTMLElement;
      laufen(e);
      if (!VORSCHAU_TAGS.has(e.localName)) {
        if (e.localName === 'img') e.remove();
        else e.replaceWith(...Array.from(e.childNodes));
        continue;
      }
      const stil = e.getAttribute('style');
      const href = e.getAttribute('href');
      const farbe = e.getAttribute('color');
      for (const at of Array.from(e.attributes)) e.removeAttribute(at.name);
      if (stil) {
        const teile = stil.split(';').map((x) => x.trim()).filter((x) => {
          const name = x.split(':')[0]?.trim().toLowerCase();
          return name && VORSCHAU_STILE.has(name) && /^[\w\s#(),.%-]+:[\w\s#(),.%-]+$/.test(x);
        });
        if (teile.length) e.setAttribute('style', teile.join('; '));
      }
      if (e.localName === 'font' && farbe && /^#?[0-9a-z]{1,20}$/i.test(farbe)) e.setAttribute('color', farbe);
      if (e.localName === 'a' && href && /^(https?:|mailto:)/i.test(href.trim())) {
        e.setAttribute('href', href.trim());
        e.setAttribute('target', '_blank');
        e.setAttribute('rel', 'noopener noreferrer');
        e.setAttribute('title', href.trim());
      }
    }
  };
  laufen(tpl.content);
  return tpl.innerHTML;
}

/** Links in einem Stück HTML: Adresse und Linktext. */
export function linksIn(html: string): { href: string; text: string }[] {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  return Array.from(tpl.content.querySelectorAll('a[href]')).map((a) => ({
    href: (a.getAttribute('href') ?? '').trim(),
    text: (a.textContent ?? '').replace(/\s+/g, ' ').trim(),
  }));
}
